import { useCallback, useEffect, useState } from 'react';
import type { ModelInfo, ModelListing } from '../types/agent';

const KEY = (email: string | null) => `sculpt_model:${email ?? 'default'}`;

export interface ModelsApi {
  getModels(): Promise<ModelListing>;
}

export interface UseModels {
  /** The models offered, each allowed or not by the user's organization; empty until loaded. */
  models: ModelInfo[];
  /** The model a new conversation runs on: the user's last pick while their organization allows it, else the
   *  server's default; null until loaded (the server then picks). */
  selected: string | null;
  choose: (id: string) => void;
  refresh: () => Promise<void>;
}

/** The model picker's state (PLAN-model-choice.md). The last pick is a per-user preference in localStorage, keyed by
 *  email: users of one machine share the bucket. Re-read on focus, so a model an admin refused in the portal greys out. */
export function useModels(api: ModelsApi, email: string | null): UseModels {
  const [listing, setListing] = useState<ModelListing | null>(null);
  const [preferred, setPreferred] = useState<string | null>(() => read(email));

  useEffect(() => setPreferred(read(email)), [email]);

  const refresh = useCallback(async () => {
    try {
      setListing(await api.getModels());
    } catch {
      /* an older server, or offline: the picker stays hidden and the server picks */
    }
  }, [api]);

  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const choose = useCallback(
    (id: string) => {
      setPreferred(id);
      try {
        localStorage.setItem(KEY(email), id);
      } catch {
        /* private mode / quota — keep in memory only */
      }
    },
    [email],
  );

  const models = listing?.models ?? [];
  const usable = models.find((m) => m.id === preferred && m.allowed);
  return { models, selected: usable ? usable.id : listing?.default ?? null, choose, refresh };
}

function read(email: string | null): string | null {
  try {
    return localStorage.getItem(KEY(email));
  } catch {
    return null;
  }
}
